import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Users, Calendar, DollarSign, MapPin, Plane, FileText, Hotel, ShieldCheck, Coffee, Moon } from 'lucide-react';
import { messageService } from '../../services/messageService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';

export default function AgentMessagesV2ChatThread() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ price: '', currency: 'GBP', notes: '' });
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    loadConversation();
    loadMessages();
    getCurrentUser();

    // Subscribe to new messages
    const channel = messageService?.subscribeToMessages(
      conversationId,
      (newMsg) => {
        setMessages(prev => [...prev, newMsg]);
        scrollToBottom();
      }
    );

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase?.auth?.getUser();
    setCurrentUserId(user?.id);
  };

  const loadConversation = async () => {
    try {
      const data = await messageService?.getConversationById(conversationId);
      setConversation(data);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError(err?.message || 'Failed to load conversation');
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await messageService?.getConversationMessages(conversationId);
      setMessages(data);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError(err?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || sending) return;

    try {
      setSending(true);
      setError('');
      await messageService?.sendMessage(conversationId, newMessage?.trim());
      setNewMessage('');
      // Message will be added via realtime subscription
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSendQuote = async (e) => {
    e?.preventDefault();
    if (!quoteForm?.price || submittingQuote) return;

    try {
      setSubmittingQuote(true);
      setError('');
      setQuoteSuccess('');

      // Get current user (agent_id)
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch agent's profile data (full name and company name)
      const { data: agentProfile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('full_name, company_name')
        ?.eq('id', user?.id)
        ?.single();

      if (profileError) {
        console.error('Error fetching agent profile:', profileError);
      }

      // Prepare metadata JSON with all quote details including agent information
      const quoteMetadata = {
        hotelName: quoteForm?.hotelName || null,
        boardBasis: quoteForm?.boardBasis || null,
        dates: {
          flexible: quoteForm?.datesFlexible || false,
          startDate: quoteForm?.startDate || null,
          endDate: quoteForm?.endDate || null
        },
        flights: {
          included: quoteForm?.flightsIncluded || false,
          departureAirport: quoteForm?.departureAirport || null,
          airline: quoteForm?.airline || null,
          flightTimes: quoteForm?.flightTimes || null
        },
        services: {
          transferIncluded: quoteForm?.transferIncluded || false,
          baggageIncluded: quoteForm?.baggageIncluded || false
        },
        additionalInclusions: quoteForm?.additionalInclusions || null,
        notes: quoteForm?.notes || null,
        agentFullName: agentProfile?.full_name || null,
        agentCompanyName: agentProfile?.company_name || null
      };

      // Update conversations table with quote information
      const { data: updatedConversation, error: updateError } = await supabase
        ?.from('conversations')
        ?.update({
          quote_price: parseFloat(quoteForm?.price),
          quote_currency: quoteForm?.currency,
          quote_submitted_at: new Date()?.toISOString(),
          metadata: quoteMetadata
        })
        ?.eq('id', conversationId)
        ?.eq('agent_id', user?.id) // Ensure agent owns this conversation
        ?.select()
        ?.single();

      if (updateError) throw updateError;

      // Reload conversation data to reflect changes
      await loadConversation();

      // Show success message
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);

      // Close modal and reset form
      setShowQuoteModal(false);
      setQuoteForm({ price: '', currency: 'GBP', notes: '' });
      setSubmittingQuote(false);

      // Optionally send a system message to the conversation
      const quoteSummary = `Quote submitted: ${quoteForm?.currency} ${parseFloat(quoteForm?.price)?.toLocaleString()}${quoteForm?.hotelName ? ` • ${quoteForm?.hotelName}` : ''}`;
      
      await supabase?.from('messages')?.insert({
        conversation_id: conversationId,
        sender_id: user?.id,
        content: quoteSummary,
        message_type: 'system'
      });

    } catch (error) {
      console.error('Error sending quote:', error);
      setError(error?.message || 'Failed to send quote. Please try again.');
      setSubmittingQuote(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatMonthSeason = (metadata) => {
    if (!metadata) return null;
    
    // Handle month/season selections from enquiry form
    if (metadata?.dateType === 'month') {
      const months = metadata?.selectedMonths || [];
      if (months?.length > 0) {
        return months?.map(m => {
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return monthNames?.[m - 1] || m;
        })?.join(', ');
      }
    }
    
    if (metadata?.dateType === 'season') {
      const seasons = metadata?.selectedSeasons || [];
      if (seasons?.length > 0) {
        return seasons?.map(s => s?.charAt(0)?.toUpperCase() + s?.slice(1))?.join(', ');
      }
    }
    
    return null;
  };

  const formatDepartureAirport = (metadata) => {
    if (!metadata) return 'Not specified';
    return metadata?.departureAirport || 'Not specified';
  };

  const formatPreferences = (metadata) => {
    if (!metadata) return [];
    
    const preferences = [];
    if (metadata?.preferences) {
      Object.entries(metadata?.preferences)?.forEach(([key, value]) => {
        if (value === true) {
          // Convert camelCase to readable format
          const readable = key?.replace(/([A-Z])/g, ' $1')?.trim();
          preferences?.push(readable?.charAt(0)?.toUpperCase() + readable?.slice(1));
        }
      });
    }
    return preferences;
  };

  const parseBudgetRange = (budgetRange) => {
    if (!budgetRange) return 'Not specified';
    if (typeof budgetRange === 'string') {
      try {
        const parsed = JSON.parse(budgetRange);
        return `${parsed?.currency || 'GBP'} ${parsed?.min || 0} - ${parsed?.max || 0}`;
      } catch {
        return budgetRange;
      }
    }
    if (typeof budgetRange === 'object') {
      return `${budgetRange?.currency || 'GBP'} ${budgetRange?.min || 0} - ${budgetRange?.max || 0}`;
    }
    return 'Not specified';
  };

  const formatTravelPreferences = (metadata) => {
    if (!metadata) return null;
    
    // Handle travel preferences from enquiry form
    if (metadata?.travelPreferences) {
      return Object.entries(metadata?.travelPreferences)?.map(([category, items]) => ({
        category,
        items: Array.isArray(items) ? items : [items]
      }));
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back to Inbox Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/agent/messages')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Inbox</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Chat Area */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 200px)' }}>
              {/* Chat Header */}
              {conversation && (
                <div className="border-b border-gray-200 p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-gray-900">
                        {conversation?.enquiry?.destination || 'Unknown Destination'}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {conversation?.enquiry?.customerName || 'Unknown Customer'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {conversation?.quoteSubmittedAt && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Quote Sent: {conversation?.quoteCurrency} {conversation?.quotePrice}
                        </span>
                      )}
                      <button
                        onClick={() => setShowQuoteModal(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <FileText className="w-4 h-4" />
                        {conversation?.quoteSubmittedAt ? 'Update Quote' : 'Send Quote'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loading && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading messages...</p>
                  </div>
                )}

                {!loading && messages?.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No messages yet. Start the conversation!</p>
                  </div>
                )}

                {!loading && messages?.map((message) => {
                  const isOwnMessage = message?.senderId === currentUserId;
                  return (
                    <div
                      key={message?.id}
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg px-4 py-2 ${
                          isOwnMessage
                            ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message?.content}</p>
                        <div
                          className={`text-xs mt-1 ${
                            isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                          }`}
                        >
                          {formatMessageTime(message?.createdAt)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="border-t border-gray-200 p-4 bg-white">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e?.target?.value)}
                    placeholder="Type your message..."
                    disabled={sending}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage?.trim()}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>Send</span>
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Enquiry Details Sidebar */}
          <div className="lg:col-span-1">
            {conversation && conversation?.enquiry && (
              <div className="bg-white rounded-lg shadow-sm p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <h3 className="text-lg font-bold text-gray-900 sticky top-0 bg-white pb-3 border-b">Enquiry Details</h3>

                <div className="space-y-4">
                  {/* Customer Name */}
                  {conversation?.enquiry?.customerName && (
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Customer</p>
                        <p className="text-sm text-gray-600 mt-1">
                          {conversation?.enquiry?.customerName}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Destination */}
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Destination</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {conversation?.enquiry?.destination}
                      </p>
                    </div>
                  </div>

                  {/* Trip Type */}
                  <div className="flex items-start gap-3">
                    <Plane className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Trip Type</p>
                      <p className="text-sm text-gray-600 mt-1 capitalize">
                        {conversation?.enquiry?.tripType?.replace('-', ' ')}
                      </p>
                    </div>
                  </div>

                  {/* Travel Dates OR Month/Season */}
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Travel Period</p>
                      {conversation?.enquiry?.departureDate && conversation?.enquiry?.returnDate ? (
                        <p className="text-sm text-gray-600 mt-1">
                          {formatDate(conversation?.enquiry?.departureDate)} - {formatDate(conversation?.enquiry?.returnDate)}
                        </p>
                      ) : (
                        <>
                          {formatMonthSeason(conversation?.enquiry?.metadata) ? (
                            <p className="text-sm text-gray-600 mt-1">
                              {formatMonthSeason(conversation?.enquiry?.metadata)}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-600 mt-1">Flexible dates</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Preferred Nights */}
                  {conversation?.enquiry?.preferredNights && (
                    <div className="flex items-start gap-3">
                      <Moon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Preferred Nights</p>
                        <p className="text-sm text-gray-600 mt-1">
                          {conversation?.enquiry?.preferredNights} night{conversation?.enquiry?.preferredNights > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Travelers */}
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Travelers</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {conversation?.enquiry?.adults} Adult{conversation?.enquiry?.adults > 1 ? 's' : ''}
                        {conversation?.enquiry?.children > 0 && `, ${conversation?.enquiry?.children} Child${conversation?.enquiry?.children > 1 ? 'ren' : ''}`}
                      </p>
                    </div>
                  </div>

                  {/* Customer Location */}
                  {conversation?.enquiry?.metadata?.customerLocation && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Customer Location</p>
                        <p className="text-sm text-gray-600 mt-1">
                          {conversation?.enquiry?.metadata?.customerLocation}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Departure Airport */}
                  {conversation?.enquiry?.metadata?.departureAirport && (
                    <div className="flex items-start gap-3">
                      <Plane className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Departure Airport</p>
                        <p className="text-sm text-gray-600 mt-1">
                          {formatDepartureAirport(conversation?.enquiry?.metadata)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Budget Range */}
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Budget Range</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {parseBudgetRange(conversation?.enquiry?.budgetRange)}
                      </p>
                    </div>
                  </div>

                  {/* Accommodation Standard */}
                  {conversation?.enquiry?.accommodationPreferences && (
                    <div className="flex items-start gap-3">
                      <Hotel className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Accommodation Standard</p>
                        <p className="text-sm text-gray-600 mt-1 capitalize">
                          {conversation?.enquiry?.accommodationPreferences}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Special Requirements / More Details */}
                  {conversation?.enquiry?.description && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-900 mb-2">More Details</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">
                        {conversation?.enquiry?.description}
                      </p>
                    </div>
                  )}

                  {/* Additional Requirements */}
                  {conversation?.enquiry?.additionalRequirements && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-900 mb-2">Special Requirements</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">
                        {conversation?.enquiry?.additionalRequirements}
                      </p>
                    </div>
                  )}

                  {/* Preferences Checkboxes */}
                  {formatPreferences(conversation?.enquiry?.metadata)?.length > 0 && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-900 mb-2">Preferences</p>
                      <div className="flex flex-wrap gap-2">
                        {formatPreferences(conversation?.enquiry?.metadata)?.map((pref, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {pref}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Travel Preferences (Activities, Transport, Dining) */}
                  {formatTravelPreferences(conversation?.enquiry?.metadata) && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-900 mb-3">Travel Interests</p>
                      <div className="space-y-3">
                        {formatTravelPreferences(conversation?.enquiry?.metadata)?.map((prefGroup, index) => (
                          <div key={index}>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">{prefGroup?.category}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {prefGroup?.items?.map((item, itemIndex) => (
                                <span
                                  key={itemIndex}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                                >
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quote Display Badge */}
        {conversation?.quote_submitted_at && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-semibold text-green-900">
                  Quote: {conversation?.quote_currency} {conversation?.quote_price?.toLocaleString()}
                </p>
                {conversation?.quote_metadata?.hotelName && (
                  <p className="text-xs text-green-700">
                    {conversation?.quote_metadata?.hotelName}
                    {conversation?.quote_metadata?.dates?.startDate && 
                     !conversation?.quote_metadata?.dates?.flexible && 
                     ` • ${new Date(conversation.quote_metadata.dates.startDate)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${new Date(conversation.quote_metadata.dates.endDate)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                    {conversation?.quote_metadata?.dates?.flexible && ' • Flexible dates'}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {showSuccessMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800">Quote sent successfully!</p>
          </div>
        )}

        {/* Quote Modal */}
        {showQuoteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">Send Quote - {conversation?.enquiry?.destination || 'Unknown Destination'}</h2>
                <button
                  onClick={() => {
                    setShowQuoteModal(false);
                    setQuoteForm({ price: '', currency: 'GBP', notes: '' });
                    setError('');
                    setQuoteSuccess('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSendQuote} className="p-6 space-y-6">
                {/* Price Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                    Quote Price
                  </h3>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={quoteForm?.price}
                        onChange={(e) => setQuoteForm(prev => ({ ...prev, price: e?.target?.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <div className="w-32">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                      <select
                        value={quoteForm?.currency}
                        onChange={(e) => setQuoteForm(prev => ({ ...prev, currency: e?.target?.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="GBP">GBP</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Accommodation Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Hotel className="w-5 h-5 text-blue-600" />
                    Accommodation
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hotel / Accommodation Name
                    </label>
                    <input
                      type="text"
                      value={quoteForm?.hotelName}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, hotelName: e?.target?.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Hilton Beach Resort"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Board Basis</label>
                    <select
                      value={quoteForm?.boardBasis}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, boardBasis: e?.target?.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select board basis</option>
                      <option value="room_only">Room Only</option>
                      <option value="bb">B&B (Bed & Breakfast)</option>
                      <option value="half_board">Half Board</option>
                      <option value="full_board">Full Board</option>
                      <option value="all_inclusive">All Inclusive</option>
                      <option value="self_catering">Self Catering</option>
                    </select>
                  </div>
                </div>

                {/* Dates Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Travel Dates
                  </h3>
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      id="datesFlexible"
                      checked={quoteForm?.datesFlexible}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, datesFlexible: e?.target?.checked }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="datesFlexible" className="text-sm font-medium text-gray-700">
                      Dates Flexible
                    </label>
                  </div>
                  {!quoteForm?.datesFlexible && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={quoteForm?.startDate}
                          onChange={(e) => setQuoteForm(prev => ({ ...prev, startDate: e?.target?.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={quoteForm?.endDate}
                          onChange={(e) => setQuoteForm(prev => ({ ...prev, endDate: e?.target?.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Flights Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Plane className="w-5 h-5 text-blue-600" />
                    Flights
                  </h3>
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      id="flightsIncluded"
                      checked={quoteForm?.flightsIncluded}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, flightsIncluded: e?.target?.checked }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="flightsIncluded" className="text-sm font-medium text-gray-700">
                      Flights Included
                    </label>
                  </div>
                  {quoteForm?.flightsIncluded && (
                    <div className="space-y-3 pl-7">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Departure Airport
                        </label>
                        <input
                          type="text"
                          value={quoteForm?.departureAirport}
                          onChange={(e) => setQuoteForm(prev => ({ ...prev, departureAirport: e?.target?.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., London Heathrow (LHR)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Airline</label>
                        <input
                          type="text"
                          value={quoteForm?.airline}
                          onChange={(e) => setQuoteForm(prev => ({ ...prev, airline: e?.target?.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., British Airways"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Flight Times
                        </label>
                        <input
                          type="text"
                          value={quoteForm?.flightTimes}
                          onChange={(e) => setQuoteForm(prev => ({ ...prev, flightTimes: e?.target?.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., Outbound 09:00, Return 17:30"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Services */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-blue-600" />
                    Additional Services
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="transferIncluded"
                        checked={quoteForm?.transferIncluded}
                        onChange={(e) => setQuoteForm(prev => ({ ...prev, transferIncluded: e?.target?.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="transferIncluded" className="text-sm font-medium text-gray-700">
                        Transfer Included
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="baggageIncluded"
                        checked={quoteForm?.baggageIncluded}
                        onChange={(e) => setQuoteForm(prev => ({ ...prev, baggageIncluded: e?.target?.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="baggageIncluded" className="text-sm font-medium text-gray-700">
                        Baggage Included
                      </label>
                    </div>
                  </div>
                </div>

                {/* Additional Inclusions */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Coffee className="w-5 h-5 text-blue-600" />
                    Additional Inclusions
                  </h3>
                  <textarea
                    value={quoteForm?.additionalInclusions}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, additionalInclusions: e?.target?.value }))}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Welcome drink on arrival, spa voucher, free WiFi..."
                  />
                </div>

                {/* Notes Section */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Additional Notes</label>
                  <textarea
                    value={quoteForm?.notes}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, notes: e?.target?.value }))}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Any additional information or conditions..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowQuoteModal(false);
                      setQuoteForm({ price: '', currency: 'GBP', notes: '' });
                      setError('');
                      setQuoteSuccess('');
                    }}
                    disabled={submittingQuote}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!quoteForm?.price || submittingQuote}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submittingQuote ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Quote
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}